import { parsePrice } from './dom.js';

/**
 * Money on the wire is a fixed-2-decimal string, never a number.
 *
 * `properties` is a ClickHouse JSON column and the type of each path is inferred once.
 * PHP and JavaScript disagree about floats: PHP encodes round(20.00, 2) as `20.0`, the
 * browser parses that to the number 20 and re-emits it as `20`. So the same order wrote a
 * Float64 from the server and an Int64 from the browser into one path, and whichever
 * arrived second was silently dropped — losing revenue rows for every order that happens
 * to land on a whole unit.
 *
 * A string has one type in both runtimes. The server half is src/Support/Money.php; the
 * two must stay in step.
 *
 * Applying this to a value the server already formatted is safe and idempotent.
 *
 * Strings go through parsePrice() rather than a comma swap. `String(v).replace(',', '.')`
 * replaces only the *first* comma, so a grouped GBP amount — `"1,299.00"` — became
 * `"1.299.00"`, which parseFloat reads as 1.299 and toFixed rounds to `"1.30"`. A 1000x
 * revenue error on one store's currency. parsePrice already knows every separator the NF
 * stores render and round-trips the wire format unchanged ("89.90", "20.0", "4990.00").
 */
export function money(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'number' ? value : parsePrice(value);

  if (parsed === undefined || !isFinite(parsed)) {
    return undefined;
  }

  return parsed.toFixed(2);
}
